import clientService from 'src/clientService';

// A clip row carries its own signed url; an uploaded-but-unanalysed row only carries a
// file id, so its audio comes from the download-url route.

export interface AudioSource {
  fileId?: string;
  clipData?: { url?: string } | null;
}

// The route signs links for an hour. Reusing one for most of that spares a round trip on
// every play, duration read and download of the same recording, so a row whose length has
// been read starts playing without waiting on the API.
const SIGNED_URL_REUSE_MS = 50 * 60 * 1000;
const signedUrls = new Map<string, { url: Promise<string | null>; value?: string; expires: number }>();

/** The link when it is already in hand (a clip's own, or one signed earlier), without waiting. */
export const peekAudioUrl = (source: AudioSource): string | null => {
  if (source.clipData?.url) return source.clipData.url;
  const cached = source.fileId ? signedUrls.get(source.fileId) : undefined;
  return cached?.value && cached.expires > Date.now() ? cached.value : null;
};

export const resolveAudioUrl = async (source: AudioSource): Promise<string | null> => {
  const clipUrl = source.clipData?.url;
  if (clipUrl) return clipUrl;
  const { fileId } = source;
  if (!fileId) return null;

  const cached = signedUrls.get(fileId);
  if (cached && cached.expires > Date.now()) return cached.url;

  const url = clientService.api
    .getDownloadUrlApiV1AdminFilesFileIdDownloadUrlGet(fileId)
    .then((response) => response.data.download_url || null);
  const entry: { url: Promise<string | null>; value?: string; expires: number } = {
    url,
    expires: Date.now() + SIGNED_URL_REUSE_MS,
  };
  signedUrls.set(fileId, entry);
  // A refused or empty answer is asked for again next time.
  const forget = () => {
    if (signedUrls.get(fileId) === entry) signedUrls.delete(fileId);
  };
  url.then((value) => {
    if (value) entry.value = value;
    else forget();
  }, forget);
  return url;
};
